export type Callback = (...args: any[]) => any;

interface IEventCallbackStore {
    [event: string]: Callback[];
}

export interface IEventEmitter<T extends string = string> {
    on(event: T, callback: Callback): this;
    on(event: T, prop: string, callback: Callback): this;

    once(event: T, callback: Callback): this;
    once(event: T, prop: string, callback: Callback): this;

    off(event: T): this;
    off(event: T, callback: Callback): this;
    off(event: T, prop: string, callback: Callback): this;

    emit(event: T, args?: any[]): this;
    emit(event: T, prop: string, args?: any[]): this;

    clearEvents(): void;
}

export class EventEmitter<T extends string> implements IEventEmitter<T> {
    private _eventCallbacks: IEventCallbackStore = {};
    private _thisArg = null;
    protected skipEmission = false;

    on(event: T, callback: Callback): this;
    on(event: T, prop: string, callback: Callback): this;
    on(event: T, propOrCallback: string | Callback, callback?: Callback): this;
    on(event: T, propOrCallback: string | Callback, callback?: Callback): this {
        if (typeof propOrCallback === 'string') {
            event = `${event}:${propOrCallback}` as T;
        } else {
            callback = propOrCallback;
        }
        if (!this._eventCallbacks[event]) {
            this._eventCallbacks[event] = [];
        }
        if (this._eventCallbacks[event].indexOf(callback!) === -1) {
            this._eventCallbacks[event].push(callback!);
        }
        return this;
    }

    once(event: T, callback: Callback): this;
    once(event: T, prop: string, callback: Callback): this;
    once(event: T, propOrCallback: string | Callback, callback?: Callback): this {
        if (typeof propOrCallback === 'string') {
            event = `${event}:${propOrCallback}` as T;
        } else {
            callback = propOrCallback;
        }
        const temporary = (args: any[]): void => {
            if (typeof callback === 'function') {
                callback.apply(this._thisArg, [args]);
            }
            this.off(event, temporary);
        };
        this.on(event, temporary);
        return this;
    }

    off(event: T, callback: Callback): this;
    off(event: T, prop: string, callback: Callback): this;
    off(event: T): this;
    off(event: T, propOrCallback?: string | Callback, callback?: Callback): this {
        if (typeof propOrCallback !== 'undefined') {
            if (typeof propOrCallback === 'string') {
                event = `${event}:${propOrCallback}` as T;
            } else {
                callback = propOrCallback;
            }
        }
        if (typeof event === 'undefined') {
            this.clearEvents();
            return this;
        }
        if (!callback) {
            delete this._eventCallbacks[event];
            return this;
        }
        const callbacks = this._eventCallbacks[event];
        if (callbacks?.length) {
            let i = callbacks.length;
            // Do this way to make sure no index is skipped when reducing size of array
            while (i-- > 0) {
                if (callbacks[i] === callback) {
                    callbacks.splice(i, 1);
                }
            }
        }

        return this;
    }

    emit(event: T, args?: any[]): this;
    emit(event: T, prop: string, args?: any[]): this;
    emit(event: T, propOrArgs?: string | any[], args?: any[]): this {
        if (this.skipEmission) {
            return this;
        }
        if (typeof propOrArgs === 'string') {
            event = `${event}:${propOrArgs}` as T;
        } else {
            args = propOrArgs;
        }

        // Clone array to make sure all events are fired even when reducing size of array (see this.once())
        const callbacks = this._eventCallbacks[event]?.slice();
        if (callbacks) {
            for (const callback of callbacks) {
                try {
                    callback.apply(this._thisArg, args || []);
                } catch (err: unknown) {
                    console.error('CallbackError: ', err);
                }
            }
        }

        return this;
    }

    clearEvents(): void {
        this._eventCallbacks = {};
    }
}
